import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as GitService from "../../services/Git";

// ==================== SSH Key Thunks ====================

export const generateSshKey = createAsyncThunk(
  "git/generateSshKey",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await GitService.generateSshKey(payload);
      return response; // Service already returns response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchAllSshKeys = createAsyncThunk(
  "git/fetchAllSshKeys",
  async (_, { rejectWithValue }) => {
    try {
      const response = await GitService.getAllSshKeys();
      return response; // Service already returns response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchSshKeyById = createAsyncThunk(
  "git/fetchSshKeyById",
  async (keyId, { rejectWithValue }) => {
    try {
      const response = await GitService.getSshKeyById(keyId);
      return response; // Service already returns response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteSshKey = createAsyncThunk(
  "git/deleteSshKey",
  async (keyId, { rejectWithValue }) => {
    try {
      const response = await GitService.deleteSshKey(keyId);
      return { ...response, keyId }; // Service already returns response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ==================== Git Config Thunks ====================

export const createGitConfig = createAsyncThunk(
  "git/createGitConfig",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await GitService.createGitConfig(payload);
      return response; // Service already returns response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchGitConfigsBySite = createAsyncThunk(
  "git/fetchGitConfigsBySite",
  async (siteId, { rejectWithValue }) => {
    try {
      const response = await GitService.getGitConfigsBySite(siteId);
      return response; // Service already returns response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateGitConfig = createAsyncThunk(
  "git/updateGitConfig",
  async ({ configId, payload }, { rejectWithValue }) => {
    try {
      const response = await GitService.updateGitConfig(configId, payload);
      return response; // Service already returns response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteGitConfig = createAsyncThunk(
  "git/deleteGitConfig",
  async (configId, { rejectWithValue }) => {
    try {
      const response = await GitService.deleteGitConfig(configId);
      return { ...response, configId }; // Service already returns response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const testGitConnection = createAsyncThunk(
  "git/testGitConnection",
  async (configId, { rejectWithValue }) => {
    try {
      const response = await GitService.testGitConnection(configId);
      return response; // Service already returns response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ==================== Deployment Thunks ====================

export const runDeploymentPrecheck = createAsyncThunk(
  "git/runDeploymentPrecheck",
  async (configId, { rejectWithValue }) => {
    try {
      const response = await GitService.runDeploymentPrecheck(configId);
      return response; // Service already returns response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const triggerDeployment = createAsyncThunk(
  "git/triggerDeployment",
  async ({ configId, payload }, { rejectWithValue }) => {
    try {
      const response = await GitService.triggerDeployment(configId, payload);
      return response; // Service already returns response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchDeploymentsBySite = createAsyncThunk(
  "git/fetchDeploymentsBySite",
  async ({ siteId, params }, { rejectWithValue }) => {
    try {
      const response = await GitService.getDeploymentsBySite(siteId, params);
      return response; // Service already returns response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchDeploymentById = createAsyncThunk(
  "git/fetchDeploymentById",
  async (deploymentId, { rejectWithValue }) => {
    try {
      const response = await GitService.getDeploymentById(deploymentId);
      return response; // Service already returns response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const rollbackDeployment = createAsyncThunk(
  "git/rollbackDeployment",
  async (deploymentId, { rejectWithValue }) => {
    try {
      const response = await GitService.rollbackDeployment(deploymentId);
      return response; // Service already returns response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ==================== Repository Info Thunks ====================

export const fetchRepositoryBranches = createAsyncThunk(
  "git/fetchRepositoryBranches",
  async (configId, { rejectWithValue }) => {
    try {
      const response = await GitService.getRepositoryBranches(configId);
      return response; // Service already returns response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchRepositoryCommits = createAsyncThunk(
  "git/fetchRepositoryCommits",
  async ({ configId, params }, { rejectWithValue }) => {
    try {
      const response = await GitService.getRepositoryCommits(configId, params);
      return response; // Service already returns response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchDeploymentQueue = createAsyncThunk(
  "git/fetchDeploymentQueue",
  async (siteId, { rejectWithValue }) => {
    try {
      const response = await GitService.getDeploymentQueue(siteId);
      return response; // Service already returns response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ==================== Initial State ====================

const initialState = {
  // SSH Keys
  sshKeys: [],
  currentSshKey: null,
  sshKeysLoading: false,
  sshKeysError: null,

  // Git Configs
  gitConfigs: [],
  currentGitConfig: null,
  gitConfigsLoading: false,
  gitConfigsError: null,

  // Connection Testing
  connectionTestResult: null,
  connectionTestLoading: false,

  // Deployments
  deployments: [],
  currentDeployment: null,
  deploymentsLoading: false,
  deploymentsError: null,
  deploymentPagination: {
    total: 0,
    page: 1,
    limit: 20,
  },

  // Pre-check
  precheckResult: null,
  precheckLoading: false,

  // Repository Info
  branches: [],
  commits: [],
  repoInfoLoading: false,

  // Deployment Queue
  deploymentQueue: [],
  queueLoading: false,
};

// ==================== Slice ====================

const gitSlice = createSlice({
  name: "git",
  initialState,
  reducers: {
    clearCurrentSshKey: (state) => {
      state.currentSshKey = null;
    },
    clearCurrentGitConfig: (state) => {
      state.currentGitConfig = null;
    },
    clearCurrentDeployment: (state) => {
      state.currentDeployment = null;
    },
    clearConnectionTestResult: (state) => {
      state.connectionTestResult = null;
    },
    clearPrecheckResult: (state) => {
      state.precheckResult = null;
    },
    updateDeploymentStatus: (state, action) => {
      const { deploymentId, status, message } = action.payload;
      const index = state.deployments.findIndex((d) => d.id === deploymentId);
      if (index !== -1) {
        state.deployments[index].status = status;
        if (message) {
          state.deployments[index].message = message;
        }
      }
      if (state.currentDeployment?.id === deploymentId) {
        state.currentDeployment.status = status;
        if (message) {
          state.currentDeployment.message = message;
        }
      }
    },
  },
  extraReducers: (builder) => {
    // ==================== SSH Keys ====================
    builder
      .addCase(generateSshKey.pending, (state) => {
        state.sshKeysLoading = true;
        state.sshKeysError = null;
      })
      .addCase(generateSshKey.fulfilled, (state, action) => {
        state.sshKeysLoading = false;
        state.sshKeys.unshift(action.payload.data);
        state.currentSshKey = action.payload.data;
      })
      .addCase(generateSshKey.rejected, (state, action) => {
        state.sshKeysLoading = false;
        state.sshKeysError = action.payload;
      });

    builder
      .addCase(fetchAllSshKeys.pending, (state) => {
        state.sshKeysLoading = true;
        state.sshKeysError = null;
      })
      .addCase(fetchAllSshKeys.fulfilled, (state, action) => {
        state.sshKeysLoading = false;
        state.sshKeys = action.payload.data || [];
      })
      .addCase(fetchAllSshKeys.rejected, (state, action) => {
        state.sshKeysLoading = false;
        state.sshKeysError = action.payload;
      });

    builder
      .addCase(fetchSshKeyById.pending, (state) => {
        state.sshKeysLoading = true;
        state.sshKeysError = null;
      })
      .addCase(fetchSshKeyById.fulfilled, (state, action) => {
        state.sshKeysLoading = false;
        state.currentSshKey = action.payload.data;
      })
      .addCase(fetchSshKeyById.rejected, (state, action) => {
        state.sshKeysLoading = false;
        state.sshKeysError = action.payload;
      });

    builder
      .addCase(deleteSshKey.pending, (state) => {
        state.sshKeysLoading = true;
      })
      .addCase(deleteSshKey.fulfilled, (state, action) => {
        state.sshKeysLoading = false;
        state.sshKeys = state.sshKeys.filter(
          (key) => key.id !== action.payload.keyId
        );
        if (state.currentSshKey?.id === action.payload.keyId) {
          state.currentSshKey = null;
        }
      })
      .addCase(deleteSshKey.rejected, (state, action) => {
        state.sshKeysLoading = false;
        state.sshKeysError = action.payload;
      });

    // ==================== Git Configs ====================
    builder
      .addCase(createGitConfig.pending, (state) => {
        state.gitConfigsLoading = true;
        state.gitConfigsError = null;
      })
      .addCase(createGitConfig.fulfilled, (state, action) => {
        state.gitConfigsLoading = false;
        state.gitConfigs.unshift(action.payload.data);
        state.currentGitConfig = action.payload.data;
      })
      .addCase(createGitConfig.rejected, (state, action) => {
        state.gitConfigsLoading = false;
        state.gitConfigsError = action.payload;
      });

    builder
      .addCase(fetchGitConfigsBySite.pending, (state) => {
        state.gitConfigsLoading = true;
        state.gitConfigsError = null;
      })
      .addCase(fetchGitConfigsBySite.fulfilled, (state, action) => {
        state.gitConfigsLoading = false;
        state.gitConfigs = action.payload.data || [];
      })
      .addCase(fetchGitConfigsBySite.rejected, (state, action) => {
        state.gitConfigsLoading = false;
        state.gitConfigsError = action.payload;
      });

    builder
      .addCase(updateGitConfig.pending, (state) => {
        state.gitConfigsLoading = true;
      })
      .addCase(updateGitConfig.fulfilled, (state, action) => {
        state.gitConfigsLoading = false;
        const index = state.gitConfigs.findIndex(
          (config) => config.id === action.payload.data.id
        );
        if (index !== -1) {
          state.gitConfigs[index] = action.payload.data;
        }
        state.currentGitConfig = action.payload.data;
      })
      .addCase(updateGitConfig.rejected, (state, action) => {
        state.gitConfigsLoading = false;
        state.gitConfigsError = action.payload;
      });

    builder
      .addCase(deleteGitConfig.pending, (state) => {
        state.gitConfigsLoading = true;
      })
      .addCase(deleteGitConfig.fulfilled, (state, action) => {
        state.gitConfigsLoading = false;
        state.gitConfigs = state.gitConfigs.filter(
          (config) => config.id !== action.payload.configId
        );
        if (state.currentGitConfig?.id === action.payload.configId) {
          state.currentGitConfig = null;
        }
      })
      .addCase(deleteGitConfig.rejected, (state, action) => {
        state.gitConfigsLoading = false;
        state.gitConfigsError = action.payload;
      });

    builder
      .addCase(testGitConnection.pending, (state) => {
        state.connectionTestLoading = true;
        state.connectionTestResult = null;
      })
      .addCase(testGitConnection.fulfilled, (state, action) => {
        state.connectionTestLoading = false;
        state.connectionTestResult = {
          ...action.payload,
          configId: action.meta.arg, // Store configId for UI display
        };
      })
      .addCase(testGitConnection.rejected, (state, action) => {
        state.connectionTestLoading = false;
        state.connectionTestResult = {
          success: false,
          error: action.payload,
          configId: action.meta.arg, // Store configId for UI display
        };
      });

    // ==================== Deployments ====================
    builder
      .addCase(runDeploymentPrecheck.pending, (state) => {
        state.precheckLoading = true;
        state.precheckResult = null;
      })
      .addCase(runDeploymentPrecheck.fulfilled, (state, action) => {
        state.precheckLoading = false;
        state.precheckResult = action.payload;
      })
      .addCase(runDeploymentPrecheck.rejected, (state, action) => {
        state.precheckLoading = false;
        state.precheckResult = {
          success: false,
          error: action.payload,
        };
      });

    builder
      .addCase(triggerDeployment.pending, (state) => {
        state.deploymentsLoading = true;
        state.deploymentsError = null;
      })
      .addCase(triggerDeployment.fulfilled, (state, action) => {
        state.deploymentsLoading = false;
        // API returns partial deployment data, don't add to list yet
        // The list will be fetched after deployment completes
        state.currentDeployment = action.payload;
      })
      .addCase(triggerDeployment.rejected, (state, action) => {
        state.deploymentsLoading = false;
        state.deploymentsError = action.payload;
      });

    builder
      .addCase(fetchDeploymentsBySite.pending, (state) => {
        state.deploymentsLoading = true;
        state.deploymentsError = null;
      })
      .addCase(fetchDeploymentsBySite.fulfilled, (state, action) => {
        state.deploymentsLoading = false;
        // API returns { success, data: [...deployments], pagination: {...} }
        state.deployments = action.payload.data || [];
        state.deploymentPagination = {
          total: action.payload.pagination?.total || 0,
          page: action.payload.pagination?.page || 1,
          limit: action.payload.pagination?.limit || 20,
        };
      })
      .addCase(fetchDeploymentsBySite.rejected, (state, action) => {
        state.deploymentsLoading = false;
        state.deploymentsError = action.payload;
      });

    builder
      .addCase(fetchDeploymentById.pending, (state) => {
        state.deploymentsLoading = true;
      })
      .addCase(fetchDeploymentById.fulfilled, (state, action) => {
        state.deploymentsLoading = false;
        state.currentDeployment = action.payload.data;
      })
      .addCase(fetchDeploymentById.rejected, (state, action) => {
        state.deploymentsLoading = false;
        state.deploymentsError = action.payload;
      });

    builder
      .addCase(rollbackDeployment.pending, (state) => {
        state.deploymentsLoading = true;
      })
      .addCase(rollbackDeployment.fulfilled, (state, action) => {
        state.deploymentsLoading = false;
        state.deployments.unshift(action.payload.data);
      })
      .addCase(rollbackDeployment.rejected, (state, action) => {
        state.deploymentsLoading = false;
        state.deploymentsError = action.payload;
      });

    // ==================== Repository Info ====================
    builder
      .addCase(fetchRepositoryBranches.pending, (state) => {
        state.repoInfoLoading = true;
      })
      .addCase(fetchRepositoryBranches.fulfilled, (state, action) => {
        state.repoInfoLoading = false;
        state.branches = action.payload.data || [];
      })
      .addCase(fetchRepositoryBranches.rejected, (state, action) => {
        state.repoInfoLoading = false;
      });

    builder
      .addCase(fetchRepositoryCommits.pending, (state) => {
        state.repoInfoLoading = true;
      })
      .addCase(fetchRepositoryCommits.fulfilled, (state, action) => {
        state.repoInfoLoading = false;
        state.commits = action.payload.data || [];
      })
      .addCase(fetchRepositoryCommits.rejected, (state, action) => {
        state.repoInfoLoading = false;
      });

    builder
      .addCase(fetchDeploymentQueue.pending, (state) => {
        state.queueLoading = true;
      })
      .addCase(fetchDeploymentQueue.fulfilled, (state, action) => {
        state.queueLoading = false;
        // API returns { data: { queue: [...] } }
        state.deploymentQueue = action.payload.data?.queue || [];
      })
      .addCase(fetchDeploymentQueue.rejected, (state, action) => {
        state.queueLoading = false;
      });
  },
});

export const {
  clearCurrentSshKey,
  clearCurrentGitConfig,
  clearCurrentDeployment,
  clearConnectionTestResult,
  clearPrecheckResult,
  updateDeploymentStatus,
} = gitSlice.actions;

export default gitSlice.reducer;
